import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Button, Divider, HiddenFromView, IconMail24, Placeholder, Spinner, Stack, Text } from '@stitch-fix/mode-react';
import { useReducedMotion } from 'framer-motion';
import React, { useEffect, useRef, useState } from 'react';
import { type Control, type FieldErrors, useForm } from 'react-hook-form';
import { z } from 'zod';
import { Link } from '@stitch-fix/sf-next';
import { ControlledTextField } from '../../../../components/Forms';
import { SSO_OPTIMIZATION_BALANCED_EMAIL_FIRST, SSO_OPTIMIZATION_CONTROL, SSO_OPTIMIZATION_SSO_FIRST, useSsoOptimizationAllocation } from '../../../../experiments/ssoOptimization';
import type { FormRegistrationError } from '../RegistrationForm';
import { EXISTING_EMAIL_ERROR_CODE, OTHER_ERROR_CODE } from '../../../../utils/requests/clientAuth/types';
import { isValidateEmailError, isValidateEmailResponseExisting, validateEmail } from '../../../../utils/requests/clientAuth/validateEmail';
import { ErrorMessage } from '../ErrorMessage';
import { SocialButtons, type SocialButtonsProps } from '../../../../components/SocialButtons';
import { Heading, PanelLayout } from '../../../../components/OnboardingNavigation';
import { isNonEmptyString } from '../../../../utils/helpers';
import styles from './styles.module.scss';
export const EMAIL_MAX_LENGTH = 75 as const;
// Wait for the mobile keyboard animation before scrolling the expanded form into view.
export const SSO_FIRST_EMAIL_FORM_SCROLL_DELAY_MS = 300 as const;
export const emailSchema = z.object({
  email: z.string({
    required_error: 'Email not valid'
  }).email({
    message: 'Email not valid'
  }).max(EMAIL_MAX_LENGTH, {
    message: `Must be fewer than ${EMAIL_MAX_LENGTH} characters`
  })
});
export type EmailSchema = z.infer<typeof emailSchema>;
type EmailFormProps = {
  currentEmail?: string;
  offerAmount?: number;
  onError?: (error: FormRegistrationError) => void;
  onSubmit?: (email: EmailSchema) => void;
  isWafEnabled?: boolean;
  isDefaultSignupExperience?: boolean;
  socialButtonsProps: SocialButtonsProps;
};
type CommonProps = {
  control: Control<EmailSchema>;
  errors: FieldErrors<EmailSchema>;
  onSubmit: (event?: React.BaseSyntheticEvent) => Promise<void>;
  validatingEmail: boolean;
  socialButtonsProps: SocialButtonsProps;
};
export const EmailForm = ({
  currentEmail = '',
  onError,
  onSubmit,
  isWafEnabled,
  isDefaultSignupExperience = false,
  socialButtonsProps
}: EmailFormProps) => {
  const [validatingEmail, setValidatingEmail] = useState(false);
  const {
    ssoOptimizationAllocation,
    allocationLoading
  } = useSsoOptimizationAllocation({
    skip: isDefaultSignupExperience
  });
  const {
    control,
    handleSubmit,
    setError,
    formState: {
      errors
    }
  } = useForm<EmailSchema>({
    resolver: zodResolver(emailSchema),
    defaultValues: {
      email: currentEmail
    }
  });
  const submit = handleSubmit(async email => {
    setValidatingEmail(true);
    try {
      const {
        data: clientAuthValidateEmailResponse
      } = await validateEmail(email, isWafEnabled);
      if (isValidateEmailResponseExisting(clientAuthValidateEmailResponse)) {
        setError('email', {
          message: EXISTING_EMAIL_ERROR_CODE
        });
        setValidatingEmail(false);
      } else {
        onSubmit?.(email);
      }
    } catch (error) {
      if (isValidateEmailError(error)) {
        onError?.({
          type: 'email',
          data: error.response.data
        });
      }
      setValidatingEmail(false);
      setError('root', {
        message: OTHER_ERROR_CODE
      });
    }
  });
  const commonProps: CommonProps = {
    control,
    errors,
    onSubmit: submit,
    validatingEmail,
    socialButtonsProps
  };
  if (allocationLoading) {
    return <LoadingState />;
  }
  switch (ssoOptimizationAllocation) {
    case SSO_OPTIMIZATION_BALANCED_EMAIL_FIRST:
      return <BalancedEmailFirstVariant {...commonProps} />;
    case SSO_OPTIMIZATION_SSO_FIRST:
      return <SsoFirstVariant {...commonProps} currentEmail={currentEmail} />;
    case SSO_OPTIMIZATION_CONTROL:
    default:
      return <ControlVariant {...commonProps} />;
  }
};
const BalancedEmailFirstVariant = ({
  control,
  errors,
  onSubmit,
  validatingEmail,
  socialButtonsProps
}: CommonProps) => <PanelLayout align={{
  sm: 'top',
  md: 'center'
}} maxWidth="424px" as="form" onSubmit={onSubmit} aria-label="form">
    <Text setting="display-medium" color="blue-45" as="h1" mb={1}>
      How would you like to get started?
    </Text>

    <ControlledTextField control={control} name="email" label="Email address" helpText={errors.email?.message === EXISTING_EMAIL_ERROR_CODE && <>
            Email is already in use.&nbsp;
            <Link href="/login">Sign in?</Link>
          </>} maxLength={EMAIL_MAX_LENGTH} mb="9px" />

    {errors.root?.message === OTHER_ERROR_CODE && <ErrorMessage />}

    <Text setting="body-small" mb={1.5}>
      By continuing, you accept Stitch Fix&apos;s{' '}
      <Link href="/terms">Terms of Use</Link> and{' '}
      <Link href="/privacy">Privacy Policy</Link>.
    </Text>

    <Button width="fill" type="submit" variant="filled-statement" disabled={validatingEmail} endIcon={validatingEmail ? <Spinner size="xsmall" /> : undefined}>
      Continue
    </Button>

    <SocialButtons {...socialButtonsProps} buttonOrder={['Google', 'Apple', 'Facebook']} divider={{
    color: 'light-gray'
  }} />

    <Text setting="body-small" mt="38px" mb={2} align="center">
      Already have an account?&nbsp;
      <Link href="/login">Sign in</Link>
    </Text>
  </PanelLayout>;
const SsoFirstVariant = ({
  control,
  errors,
  onSubmit,
  validatingEmail,
  socialButtonsProps,
  currentEmail
}: CommonProps & {
  currentEmail?: string;
}) => {
  const [isEmailFormExpanded, setIsEmailFormExpanded] = useState(isNonEmptyString(currentEmail));
  const shouldReduceMotion = useReducedMotion();
  const emailFormBottomRef = useRef<HTMLDivElement>(null);
  const emailFormId = 'email-form';
  useEffect(() => {
    if (!isEmailFormExpanded) return undefined;
    const timeoutId = window.setTimeout(() => {
      emailFormBottomRef.current?.scrollIntoView?.({
        behavior: shouldReduceMotion ? 'auto' : 'smooth',
        block: 'end',
        inline: 'nearest'
      });
    }, SSO_FIRST_EMAIL_FORM_SCROLL_DELAY_MS);
    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [isEmailFormExpanded, shouldReduceMotion]);
  return <PanelLayout align={{
    sm: 'top',
    md: 'center'
  }} maxWidth="424px" as="form" onSubmit={onSubmit} aria-label="form">
      <Box
    // Keeps the form from jumping around when the email form is expanded
    minHeight={{
      md: '580px',
      lg: '625px'
    }}>
        <Text setting="display-medium" color="blue-45" as="h1" mb={1}>
          How would you like to get started?
        </Text>

        <Text setting="body-small" mb={2}>
          By continuing, you accept Stitch Fix&apos;s{' '}
          <Link href="/terms">Terms of Use</Link> and{' '}
          <Link href="/privacy">Privacy Policy</Link>.
        </Text>

        <SocialButtons {...socialButtonsProps} buttonOrder={['Google', 'Apple', 'Facebook']} divider={false} />

        {!isEmailFormExpanded && <Button type="button" variant="social" width="fill" startIcon={<IconMail24 />} aria-controls={emailFormId} aria-expanded={isEmailFormExpanded} onClick={() => setIsEmailFormExpanded(true)} mt={1}>
            Continue with Email
          </Button>}

        <Box id={emailFormId}>
          {isEmailFormExpanded && <Box className={styles.expandedEmailForm}>
              <Divider variant="section" color="light-gray" my={2}>
                <Text setting="eyebrow-small" as="span">
                  or
                </Text>
              </Divider>

              <ControlledTextField control={control} name="email" label="Email address" helpText={errors.email?.message === EXISTING_EMAIL_ERROR_CODE && <>
                      Email is already in use.&nbsp;
                      <Link href="/login">Sign in?</Link>
                    </>} maxLength={EMAIL_MAX_LENGTH} autoFocus />

              {errors.root?.message === OTHER_ERROR_CODE && <ErrorMessage />}

              <Button width="fill" type="submit" variant="filled-statement" disabled={validatingEmail} endIcon={validatingEmail ? <Spinner size="xsmall" /> : undefined}>
                Continue
              </Button>
            </Box>}
        </Box>

        <Text setting="body-small" mt="38px" mb={2} align="center">
          Already have an account?&nbsp;
          <Link href="/login">Sign in</Link>
        </Text>

        <div ref={emailFormBottomRef} id={`${emailFormId}-bottom`} style={{
        scrollMarginBottom: '16px'
      }} aria-hidden />
      </Box>
    </PanelLayout>;
};
const ControlVariant = ({
  control,
  errors,
  onSubmit,
  validatingEmail,
  socialButtonsProps
}: CommonProps) => <PanelLayout maxWidth="424px" as="form" onSubmit={onSubmit} aria-label="form">
    <Heading title="Before we begin, what's your email?" />

    <ControlledTextField control={control} name="email" label="Email" helpText={errors.email?.message === EXISTING_EMAIL_ERROR_CODE && <>
            Email is already in use.&nbsp;
            <Link href="/login">Sign in?</Link>
          </>} maxLength={EMAIL_MAX_LENGTH} placeholder="email@example.com" />

    {errors.root?.message === OTHER_ERROR_CODE && <ErrorMessage />}

    <Text setting="body-small" mb={1.5} mt="14px">
      By continuing, you accept Stitch Fix&apos;s{' '}
      <Link href="/terms">Terms of Use</Link> and{' '}
      <Link href="/privacy">Privacy Policy</Link>.
    </Text>

    <Button width="fill" type="submit" variant="filled-statement-large" disabled={validatingEmail} endIcon={validatingEmail ? <Spinner size="xsmall" /> : undefined} mb={3}>
      Continue
    </Button>

    <SocialButtons {...socialButtonsProps} buttonOrder={['Apple', 'Google', 'Facebook']} inline />

    <Text setting="body-small" mt="20px">
      Already have an account?&nbsp;
      <Link href="/login">Sign in</Link>
    </Text>
  </PanelLayout>;
const LoadingState = () => {
  return <PanelLayout maxWidth="424px">
      <HiddenFromView>Loading...</HiddenFromView>

      <Stack spacing={1}>
        <Text setting="title-xxlarge">
          <Placeholder />
          <Placeholder width="50%" />
        </Text>

        <Placeholder variant="rect" width="100%" height="58px" mt="50px" />

        <Text setting="body-small">
          <Placeholder />
          <Placeholder width="50%" />
        </Text>

        <Placeholder variant="rect" width="100%" height="44px" />

        <Text setting="body-small" mt="50px">
          <Placeholder />
        </Text>

        <Placeholder variant="rect" width="100%" height="44px" />
      </Stack>
    </PanelLayout>;
};